import React from "react";
import ComponentDecorator from "./element";
import Events from "ampersand-events";

class LayoutManager {
    // Getters / Setters

    /*
     *  Size
     *  Returns the total amount of components
     */
    get size() {
        return this._components.length;
    }

    /*
     *  Scroll hook
     */
    onScroll() {
        // no-op
    }

    /*
     *  Height
     *  Returns how many pixels the runway is
     */
    get runwayLength() {
        return this._cursor[1];
    }

    get hasDragAndDrop() {
        return this._hasDragAndDrop;
    }

    constructor(settings = {}) {
        const { componentTypes, dragDrop, onScroll, isTouch } = settings;

        // for DnD
        this._moveComponent = this._moveComponent.bind(this);
        this.handleBeginDrag = this.handleBeginDrag.bind(this);
        this._dndEnabled = false;
        this._hasDragAndDrop = dragDrop || false;

        if (onScroll) {
            this.onScroll = onScroll;
        }
        this.isTouch = isTouch;

        this._cursor = [0, 0];
        this._components = [];
        this._visibleObjects = [];
        this.scrollBuffer = 0.4;
        this._scrollLeft = 0;

        if (componentTypes) {
            this._componentTypes = {};
            const keys = Object.keys(componentTypes);
            if (keys.length === 0) {
                throw new Error("componentTypes invalid");
            }

            keys.forEach((key) => {
                const componentType = componentTypes[key];
                componentType.Component = ComponentDecorator(componentType.Component, this._hasDragAndDrop);
                this._componentTypes[key] = componentType;
            });
        } else {
            throw new Error("componentTypes is required");
        }
    }

    enableDragDrop() {
        this._dndEnabled = true;
    }

    disableDragDrop() {
        this._dndEnabled = false;
    }

    reset() {
        this._cursor = [0, 0];
        this._components = [];
        this._visibleObjects = [];
        this._visibleComponents = null;

        this._previousRowHeight = null;
        this._lastStartPosition = 0;
        this._lastEndPosition = 0;
        this.trigger("refresh");
    }

    _positionComponent(width, height, horizontalSpacing) {
        let [x, y] = this._cursor;

        let xNext = x;
        let yNext = y;

        if (width && width !== "auto") {
            xNext = xNext + width + horizontalSpacing;
            if (xNext + width > this._viewport.width) {
                this._previousRowHeight = null;
                xNext = 0;
                yNext = y + height;
            } else {
                this._previousRowHeight = height;
            }
        } else {
            // We need to move the cursor down
            if (this._previousRowHeight) {
                y = y + this._previousRowHeight;
                this._previousRowHeight = null;
            }
            x = 0;
            xNext = 0;
            yNext = y + height;
        }

        // Move cursor to next position
        this._cursor = [xNext, yNext];

        return [x, y];
    }

    addComponent(componentProps, componentType, index = false) {
        const { width, height, horizontalSpacing } = this._componentTypes[componentType];
        const component = {
            type: componentType,
            props: componentProps,
            index: index || this._components.length,
            width: width,
            height,
            horizontalSpacing,
        };

        if (index === false) {
            component.position = this._positionComponent(width, height, horizontalSpacing);
            this._components.push(component);
        } else {
            this._components.splice(index, 0, component);
            this._rebuildLayout(index - 1);
            this.trigger("refresh");
        }
    }

    removeComponentByProp(prop, value) {
        this._components.some((component, i) => {
            if (component.props[prop] === value) {
                this._components.splice(i, 1);
                this._rebuildLayout(i - 1);
                this.trigger("remove");
                return true;
            }
            return false;
        });
    }

    // Internal to infinite scroll container

    _moveComponent(dragIndex, hoverIndex, drop = false) {
        if (drop) {
            this.trigger("drop", dragIndex, hoverIndex);
        } else {
            const dragComponent = this._components[dragIndex];
            this._components.splice(dragIndex, 1);
            this._components.splice(hoverIndex, 0, dragComponent);
            this._rebuildLayout(Math.min(dragIndex, hoverIndex) - 1);
            this.trigger("move", dragIndex, hoverIndex);
        }
    }

    handleBeginDrag(dragIndex) {
        this.trigger("drag", dragIndex);
    }

    _getComponent(component, componentIndex) {
        const { type, props, index, position, scrollLeft, width, height, horizontalSpacing } = component;

        const componentType = this._componentTypes[type];
        const { Component, customDragSource } = componentType;

        return (
            <Component
                key={`${type}-${componentIndex}`}
                index={index}
                x={position[0]}
                y={position[1]}
                scrollLeft={scrollLeft}
                width={width}
                height={height}
                horizontalSpacing={horizontalSpacing}
                customDragSource={customDragSource}
                moveComponent={this._moveComponent}
                onBeginDrag={this.handleBeginDrag}
                dragDrop={this._dndEnabled}
                isTouch={this.isTouch}
                {...props}
            />
        );
    }

    _getVisibleStart(scrollHeight) {
        const computedScrollHeight = scrollHeight - this._viewport.offset - this.scrollBufferPixels;
        let index = Math.max(Math.min(this._components.length - 1, this._lastStartPosition || 0), 0);

        let component = this._components[index];

        if (component) {
            let lastY = null;
            if (component.position[1] > computedScrollHeight) {
                // go backwards

                do {
                    index = index - 1;
                    lastY = component.position[1];
                    component = this._components[index];
                    if (!component) {
                        index = index + 1;
                        break;
                    }
                } while (component.position[1] > computedScrollHeight || lastY === component.position[1]);
            } else {
                do {
                    index = index + 1;
                    component = this._components[index];
                    if (!component) {
                        break;
                    }
                } while (component.position[1] <= computedScrollHeight);

                index = index - 1;
                component = this._components[index];

                lastY = component.position[1];
                // make sure we get the first one with this height
                do {
                    index = index - 1;
                    lastY = component.position[1];
                    component = this._components[index];
                    if (!component) {
                        break;
                    }
                } while (lastY === component.position[1]);

                index = index + 1;
            }
        }

        if (index < 0) {
            index = 0;
        }

        return index;
    }

    _getVisibleEnd(scrollHeight) {
        const lastIndex = Math.max(this._components.length - 1, 0);
        let index = Math.min(lastIndex, this._lastEndPosition || 0);
        let component = this._components[index];
        const bottomHeight = scrollHeight + this._viewport.height - this._viewport.offset + this.scrollBufferPixels;

        if (component) {
            if (component.position[1] < bottomHeight) {
                // go
                do {
                    index = index + 1;
                    component = this._components[index];
                    if (!component) {
                        index = index - 1;
                        break;
                    }
                } while (component.position[1] < bottomHeight);
            } else {
                // back we go
                do {
                    index = index - 1;
                    component = this._components[index];
                    if (!component) {
                        break;
                    }
                } while (component.position[1] >= bottomHeight);
                index = index + 1;
            }
        }

        // since this is a slice, we need to add one
        index = index + 1;

        if (index < 0) {
            index = 0;
        } else if (index >= this._components.length) {
            index = this._components.length;
        }

        return index;
    }

    getVisibleComponents(scrollHeight, scrollLeft, clearCache = false) {
        // Make sure we know the world
        if (!this._viewport) {
            return [];
        }

        // Figure out what to display
        const visibleStart = this._getVisibleStart(scrollHeight);
        const visibleEnd = this._getVisibleEnd(scrollHeight);
        // grab from cache if there isnt a change
        if (
            !clearCache &&
            this._visibleComponents &&
            visibleStart === this._lastStartPosition &&
            visibleEnd === this._lastEndPosition &&
            this._scrollLeft === scrollLeft
        ) {
            return this._visibleComponents;
        }

        const visibleObjects = this._components.slice(visibleStart, visibleEnd);
        this._lastStartPosition = visibleStart;
        this._lastEndPosition = visibleEnd;
        this._scrollLeft = scrollLeft;

        // Hydrate children with data (this is done so we can hopefully reuse some components but just with different props)
        const componentsCounter = {};
        Object.keys(this._componentTypes).forEach((key) => {
            componentsCounter[key] = {
                renderedIndexes: [],
                renderedComponents: [],
                counter: 0,
            };
        });

        // Try not to render as much as possible

        visibleObjects.forEach((component) => {
            if (this._visibleObjects.indexOf(component) !== -1) {
                componentsCounter[component.type].renderedIndexes.push(component.renderedIndex);
                componentsCounter[component.type].renderedComponents.push(component);
            }
        });

        const componentsToRender = [];

        visibleObjects.forEach((component) => {
            // only render new components
            if (this._visibleObjects.indexOf(component) === -1) {
                // get index, if its already in use, skip it
                let componentIndex;
                do {
                    componentIndex = componentsCounter[component.type].counter;
                    componentsCounter[component.type].counter = componentsCounter[component.type].counter + 1;
                } while (componentsCounter[component.type].renderedIndexes.indexOf(componentIndex) !== -1);

                component.renderedIndex = componentIndex;
                component.scrollLeft = scrollLeft;
                componentsToRender.push(this._getComponent(component, componentIndex));
            }
        });

        // render already rendered components (react will know not to change them)
        Object.keys(componentsCounter).forEach((componentType) => {
            componentsCounter[componentType].renderedComponents.forEach((component) => {
                component.scrollLeft = scrollLeft;
                componentsToRender.push(this._getComponent(component, component.renderedIndex));
            });
        });

        // Cache for next time so we can see what has already been ready
        this._visibleObjects = visibleObjects;
        this._visibleComponents = componentsToRender;

        return componentsToRender;
    }

    _rebuildLayout(index = 0) {
        this._previousRowHeight = null;
        if (index <= 0) {
            index = 0;
            this._cursor = [0, 0];
        } else if (this._components[index]) {
            this._cursor = this._components[index].position;
        } else if (this._components[index] - 1) {
            this._cursor = this._components[index - 1].position;
        } else {
            this._cursor = [0, 0];
        }
        this._components.slice(index).forEach((component, i) => {
            const { width, height, horizontalSpacing } = this._componentTypes[component.type];
            component.position = this._positionComponent(width, height, horizontalSpacing);
            component.index = index + i;
        });
    }

    // Set everytime the viewport changes by the infinite scroll container
    setViewportDimensions(width, height, offset) {
        this._viewport = { width, height, offset };
        this.scrollBufferPixels = height * this.scrollBuffer;
        this._rebuildLayout(); // reposition everything
    }
}

Object.assign(LayoutManager.prototype, Events);

export default LayoutManager;
